import type { MoneyEntry } from '../api/financeTypes'

/**
 * Money helpers for the admin finance views. The API sends amounts as decimal
 * STRINGS in major units; nothing here ever turns one into a float. Arithmetic
 * (a percentage, a bar ratio, a same-currency total) goes through BigInt on
 * the scaled digits, and amounts of DIFFERENT currencies are never combined
 * (owner 2026-09-28: no FX) — `addMoney` throws instead of guessing.
 */

export const DASH = '—'
const NBSP = ' '
const DECIMAL = /^(-)?(\d+)(?:\.(\d+))?$/

interface Parts {
  neg: boolean
  int: string
  frac: string
}

function parts(amount: string | null | undefined): Parts | null {
  if (amount === null || amount === undefined) return null
  const m = DECIMAL.exec(String(amount).trim())
  if (!m) return null
  return { neg: m[1] === '-', int: m[2]!.replace(/^0+(?=\d)/, ''), frac: m[3] ?? '' }
}

/** Hungarian number format straight from the decimal string: `79.00` ->
 * `79,00`, `14365.5` -> `14 365,50`, `0.00041000` -> `0,00041000` (the digits
 * the API sent are kept, at least two decimals). Not parseable -> `—`. */
export function formatAmount(amount: string | null | undefined): string {
  const p = parts(amount)
  if (!p) return DASH
  const grouped = p.int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
  const frac = p.frac.padEnd(2, '0')
  const isZero = /^0*$/.test(p.int) && /^0*$/.test(frac)
  return `${p.neg && !isZero ? '−' : ''}${grouped},${frac}`
}

/** `79,00 USD`; missing amount -> `—` (never `0`). */
export function formatMoney(amount: string | null | undefined, currency: string | null | undefined): string {
  const a = formatAmount(amount)
  if (a === DASH) return DASH
  return currency ? `${a}${NBSP}${currency}` : a
}

export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH
  return String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
}

const SCALE = 12

function scaled(amount: string): bigint | null {
  const p = parts(amount)
  if (!p) return null
  const frac = p.frac.padEnd(SCALE, '0').slice(0, SCALE)
  const v = BigInt(p.int + frac)
  return p.neg ? -v : v
}

/** Decimal-safe comparison: -1 / 0 / 1; null when either side is not a decimal. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 | null {
  const x = scaled(a)
  const y = scaled(b)
  if (x === null || y === null) return null
  return x < y ? -1 : x > y ? 1 : 0
}

export function isNegative(amount: string | null | undefined): boolean {
  const s = amount == null ? null : scaled(amount)
  return s !== null && s < 0n
}

/** A string whose plain lexicographic order equals the numeric order — what a
 * sortable column hands the table so money is never compared as a float. */
export function decimalSortKey(amount: string | null | undefined): string | null {
  const s = amount == null ? null : scaled(amount)
  if (s === null) return null
  const OFFSET = 10n ** 40n // keeps negatives positive: order preserved, fixed width
  return (OFFSET + s).toString().padStart(42, '0')
}

/** Sort key of a per-currency list: its FIRST entry (the primary currency as
 * the API lists it). Different currencies are not comparable; this only gives
 * the column a stable order. */
export function primarySortKey(list: MoneyEntry[] | null | undefined): string | null {
  const first = list?.[0]
  return first ? decimalSortKey(first.amount) : null
}

/** Sum of two amounts of the SAME currency. Throws on a currency mismatch
 * instead of silently adding apples to oranges. */
export function addMoney(a: { amount: string; currency: string }, b: { amount: string; currency: string }): { amount: string; currency: string } {
  if (a.currency !== b.currency) throw new Error(`cannot add ${a.currency} to ${b.currency} (no FX)`)
  const x = scaled(a.amount)
  const y = scaled(b.amount)
  if (x === null || y === null) throw new Error('not a decimal amount')
  return { currency: a.currency, amount: unscale(x + y) }
}

function unscale(v: bigint): string {
  const neg = v < 0n
  const digits = (neg ? -v : v).toString().padStart(SCALE + 1, '0')
  const int = digits.slice(0, -SCALE)
  const frac = digits.slice(-SCALE).replace(/0+$/, '').padEnd(2, '0')
  return `${neg ? '-' : ''}${int}.${frac}`
}

/** Groups entries by currency, summing within a currency only. */
export function totalsByCurrency(entries: { amount: string; currency: string }[]): { amount: string; currency: string }[] {
  const out = new Map<string, { amount: string; currency: string }>()
  for (const e of entries) {
    const cur = out.get(e.currency)
    out.set(e.currency, cur ? addMoney(cur, e) : { currency: e.currency, amount: unscale(scaled(e.amount) ?? 0n) })
  }
  return [...out.values()]
}

/** `(cur - prev) / prev` in percent with one decimal, decimal-safe; null when
 * either side is missing or the previous value is zero. */
export function percentChange(cur: string | null | undefined, prev: string | null | undefined): number | null {
  const c = cur == null ? null : scaled(cur)
  const p = prev == null ? null : scaled(prev)
  if (c === null || p === null || p === 0n) return null
  const den = p < 0n ? -p : p
  const num = (c - p) * 1000n
  // round half away from zero, still in integers
  const tenths = (num * 2n + (num < 0n ? -den : den)) / (2n * den)
  return Number(tenths) / 10
}

export function formatPercent(p: number | null): string {
  if (p === null) return DASH
  const s = Math.abs(p).toFixed(1).replace('.', ',')
  return `${p > 0 ? '▲ ' : p < 0 ? '▼ ' : ''}${s}%`
}

/** `part / whole` in 0..1000 (permille), decimal-safe — for bar widths. */
export function ratioPermille(part: string, whole: string): number {
  const a = scaled(part)
  const b = scaled(whole)
  if (a === null || b === null || b <= 0n || a <= 0n) return 0
  const r = Number((a * 1000n) / b)
  return Math.min(1000, r)
}

/** Looks up the entry of one currency in a per-currency list. */
export function entryFor(list: MoneyEntry[] | null | undefined, currency: string): MoneyEntry | undefined {
  return list?.find((e) => e.currency === currency)
}
